import { PostfixCriteriaSchema } from 'api/apiTypes/postfixCriteria';
import { PostfixFunctionSchema } from 'api/apiTypes/postfixLogic';
import { z } from 'zod';

import { generateRuleId } from 'utils/consentMappings/generateRuleId';

/**
 * The base rule object
 * @example
 * {
 *  "ruleId": "id",
 *  "category": "category",
 *  "generatedFunction": {
 *    $function: "function() { return true; }"
 *  }
 * }
 */
export const ApiPrivacyConsentConfigurationMappingsRuleBaseSchema = z.object({
  ruleId: z.string().default(() => generateRuleId()),
  ruleName: z.string().optional().nullable(),
  categoryId: z.string().optional().nullable(),
  logic: z.union([PostfixFunctionSchema, PostfixCriteriaSchema]),
});

export type ApiPrivacyConsentConfigurationMappingsRuleBase = z.infer<
  typeof ApiPrivacyConsentConfigurationMappingsRuleBaseSchema
>;

/**
 * The default rule object
 * @example
 * {
 *  "ruleId": "id",
 *  "category": "category",
 *  "generatedFunction": {
 *    $function: "function() { return true; }"
 *  }
 * }
 */
export const ApiPrivacyConsentConfigurationMappingsRuleDefaultSchema =
  ApiPrivacyConsentConfigurationMappingsRuleBaseSchema;

export type ApiPrivacyConsentConfigurationMappingsRuleDefault = z.infer<
  typeof ApiPrivacyConsentConfigurationMappingsRuleDefaultSchema
>;

/**
 * The custom rule object
 * @example
 * {
 *  "ruleId": "id",
 *  "name": "name",
 *  "value": "value",
 *  "operator": "operator",
 *  "category": "category",
 *  "generatedFunction": {
 *    $function: "function() { return true; }"
 *  }
 * }
 */
export const ApiPrivacyConsentConfigurationMappingsRuleCustomSchema =
  ApiPrivacyConsentConfigurationMappingsRuleBaseSchema;

export type ApiPrivacyConsentConfigurationMappingsRuleCustom = z.infer<
  typeof ApiPrivacyConsentConfigurationMappingsRuleCustomSchema
>;

/**
 * The rule object
 */
export const ApiPrivacyConsentConfigurationMappingsRuleSchema = z.union([
  ApiPrivacyConsentConfigurationMappingsRuleDefaultSchema,
  ApiPrivacyConsentConfigurationMappingsRuleCustomSchema,
]);

export type ApiPrivacyConsentConfigurationMappingsRule = z.infer<
  typeof ApiPrivacyConsentConfigurationMappingsRuleSchema
>;

/**
 * The base rule object
 * @example
 * {
 *  "name": "name",
 *  "technologyId": "technologyId",
 * }
 */
export const ApiPrivacyConsentConfigurationMappingsBaseSchema = z.object({
  tagId: z.string(),
  tagName: z.string(),
  technologyId: z.string(),
  technologyName: z.string(),
});

export type ApiPrivacyConsentConfigurationMappingsBase = z.infer<
  typeof ApiPrivacyConsentConfigurationMappingsBaseSchema
>;

/**
 * The base rule object
 * @example
 * {
 *   "name": "name",
 *   "technologyId": "technologyId",
 *   "type": "default",
 *   "rules": [
 *     {
 *       "ruleId": "id",
 *       "category": "category",
 *       "generatedFunction": {
 *         $function: "function() { return true; }"
 *       }
 *     }
 *   ]
 * }
 */
export const ApiPrivacyConsentConfigurationMappingsDefaultSchema =
  ApiPrivacyConsentConfigurationMappingsBaseSchema.extend({
    type: z.literal('default').optional().nullable(),
    rules: z.array(ApiPrivacyConsentConfigurationMappingsRuleDefaultSchema).optional().nullable(),
  }).transform((data) => {
    const newRules = data.rules?.filter((x) => x.categoryId);
    return {
      ...data,
      type: newRules?.length ? ('default' as const) : undefined,
      rules: newRules?.length ? newRules : undefined,
    };
  });

/**
 * The base rule object
 * @example
 * {
 *   "name": "name",
 *   "technologyId": "technologyId",
 *   "type": "default",
 *   "rules": [
 *     {
 *       "ruleId": "id",
 *       "name": "name",
 *       "value": "value",
 *       "operator": "operator",
 *       "category": "category",
 *       "generatedFunction": {
 *         $function: "function() { return true; }"
 *       }
 *     }
 *   ]
 * }
 */
export const ApiPrivacyConsentConfigurationMappingsCustomSchema =
  ApiPrivacyConsentConfigurationMappingsBaseSchema.extend({
    type: z.literal('custom').optional().nullable(),
    rules: z.array(ApiPrivacyConsentConfigurationMappingsRuleCustomSchema).optional().nullable(),
  }).transform((data) => {
    return {
      ...data,
      type: data.rules ? data.type : undefined,
    };
  });

/**
 * The mappings object
 */
export const ApiPrivacyConsentConfigurationMappingsSchema = z.union([
  ApiPrivacyConsentConfigurationMappingsDefaultSchema,
  ApiPrivacyConsentConfigurationMappingsCustomSchema,
]);

type ApiPrivacyConsentConfigurationMappings = z.infer<typeof ApiPrivacyConsentConfigurationMappingsSchema>;

export default ApiPrivacyConsentConfigurationMappings;
